import { useEffect, useRef } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  CARGO_PROBLEM_WORKSPACE_UPDATED,
  LEAD_NOTIFICATIONS_UPDATED,
  LEADS_UPDATED,
  NOTIFICATIONS_UPDATED,
  REQUEST_CHAT_READ_UPDATED,
  REQUEST_CHAT_UPDATED,
  REQUESTS_UPDATED,
  RUSSIA_REQUEST_CHAT_READ_UPDATED,
  RUSSIA_REQUEST_CHAT_UPDATED,
  RUSSIA_REQUESTS_UPDATED,
} from '@magimp/shared-realtime';
import { useAuthStore } from '../stores/auth.js';
import { useToastStore } from '../stores/toast.js';
import { useNotificationPreferences } from '../features/notifications/index.js';
import { useRequestUrlState } from '../features/requests/useRequestUrlState.js';
import { ApiClientError } from '../api/errors.js';
import { coordinatedRefresh } from '../api/refresh.js';
import { clearWithBroadcast } from '../api/authEvents.js';
import { disconnectRealtimeSocket, getHasConnectedBefore, getRealtimeSocket } from './socket.js';
import { invalidateRequestCaches } from './invalidateRequestCaches.js';
import { createNotificationsUpdatedHandler } from './notificationsRealtimeHandler.js';
import { createChinaRealtimeHandlers } from './chinaRealtimeHandlers.js';
import { createRussiaRealtimeHandlers } from './russiaRealtimeHandlers.js';
import { useLifecycleCatchUp } from './useLifecycleCatchUp.js';
import { createLeadRealtimeHandlers } from './leadRealtimeHandlers.js';
import { createCargoProblemWorkspaceUpdatedHandler } from './cargoProblemRealtimeHandler.js';

/**
 * @aiPurpose mounts inside `AppShell` once a session exists. Establishes
 *   the singleton Socket.IO connection, subscribes to realtime events,
 *   validates payloads through strict Zod schemas, and handles reconnect
 *   catch-up and server-forced disconnect recovery.
 * @aiInvariant payload validation is mandatory — any non-conforming
 *   shape (extra keys, wrong reason, malformed id) is dropped
 *   silently. A regression on the backend gateway therefore degrades
 *   to "no realtime invalidation" rather than "polluted query cache".
 * @aiSecurity does not derive permission from the wire payload; the
 *   subsequent `queryClient.invalidateQueries` triggers REST fetches
 *   that re-apply server-side row-level visibility. A user without
 *   read access to the row simply re-receives a 404 and the cache
 *   remains empty.
 */

export function RealtimeBootstrap() {
  const queryClient = useQueryClient();
  const accessToken = useAuthStore((s) => s.accessToken);
  const pendingRecoveryRef = useRef(false);
  const needsReconnectRef = useRef(false);
  // INVARIANT (#1159 PR5): toast + URL writers must be captured in
  //   refs so the socket-listener body reads the latest snapshots
  //   without re-binding when consumers re-render the router. The
  //   handler stays bound for the lifetime of the session.
  const { t } = useTranslation('requests');
  const showToast = useToastStore((s) => s.show);
  const showToastRef = useRef(showToast);
  showToastRef.current = showToast;
  const tRef = useRef(t);
  tRef.current = t;
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  // WHY (#1073): warm the per-user notification preferences cache once
  //   the session exists so the realtime sound handler can read the
  //   user's group enable + sound key without a settings-page visit.
  //   The hook gates on `accessToken` already (query disabled when no
  //   session), and the cache slot is shared with the settings panel.
  useNotificationPreferences();

  // INVARIANT: RealtimeBootstrap is mounted globally under `AppShell`,
  // so its handlers fire on EVERY route — `/users`, `/admin`,
  // `/china-rates`, etc. URL writes from a
  // `requests:updated { reason: 'deleted' }` event must therefore be
  // gated by both:
  //   (a) `pathname === '/china-rates'` — never mutate a non-list URL.
  //   (b) the URL's current `?openId=` matches the deleted id —
  //       `useRequestsStore.selectedId` is insufficient because the
  //       passive mirror can carry a stale id from a prior /china-rates
  //       visit.
  // Both values are captured in refs so the socket handler reads the
  // latest snapshot without forcing the `useEffect` to re-bind on every
  // render (the listener stays bound across renders for the lifetime of
  // the session).
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const urlState = useRequestUrlState();
  const pathnameRef = useRef(location.pathname);
  const searchParamsRef = useRef(searchParams);
  const closeRequestRef = useRef(urlState.closeRequest);
  useEffect(() => {
    pathnameRef.current = location.pathname;
  }, [location.pathname]);
  useEffect(() => {
    searchParamsRef.current = searchParams;
  }, [searchParams]);
  useEffect(() => {
    closeRequestRef.current = urlState.closeRequest;
  }, [urlState.closeRequest]);

  useEffect(() => {
    if (!accessToken) {
      disconnectRealtimeSocket();
      return;
    }
    const socket = getRealtimeSocket();

    // Reconnect a socket that was left disconnected after a failed
    // server-forced-disconnect recovery (e.g. 429, 5xx, network error
    // on the previous refresh attempt). The REST path has now refreshed
    // the access token, so we have a fresh credential to try.
    if (needsReconnectRef.current && !socket.connected) {
      needsReconnectRef.current = false;
      socket.connect();
    }
    const china = createChinaRealtimeHandlers({
      queryClient,
      pathnameRef,
      searchParamsRef,
      closeRequestRef,
      showToastRef,
      tRef,
    });
    const russia = createRussiaRealtimeHandlers({
      queryClient,
      pathnameRef,
      navigateRef,
      showToastRef,
      tRef,
    });
    // INVARIANT (#1072): the notifications wire handler owns active-context
    //   suppression, sound playback, and the by-target mark-viewed write.
    //   See `./notificationsRealtimeHandler.ts` for the full contract.
    const notificationsUpdatedHandler = createNotificationsUpdatedHandler(queryClient);
    const leads = createLeadRealtimeHandlers(queryClient);
    const cargoProblemWorkspaceHandler = createCargoProblemWorkspaceUpdatedHandler(queryClient);
    socket.on(CARGO_PROBLEM_WORKSPACE_UPDATED, cargoProblemWorkspaceHandler);
    socket.on(LEADS_UPDATED, leads.leadsUpdatedHandler);
    socket.on(LEAD_NOTIFICATIONS_UPDATED, leads.leadNotificationsUpdatedHandler);
    socket.on(REQUESTS_UPDATED, china.requestsUpdatedHandler);
    socket.on(REQUEST_CHAT_UPDATED, china.chatUpdatedHandler);
    socket.on(REQUEST_CHAT_READ_UPDATED, china.chatReadUpdatedHandler);
    socket.on(RUSSIA_REQUESTS_UPDATED, russia.russiaRequestsUpdatedHandler);
    socket.on(RUSSIA_REQUEST_CHAT_UPDATED, russia.russiaRequestChatUpdatedHandler);
    socket.on(RUSSIA_REQUEST_CHAT_READ_UPDATED, russia.russiaRequestChatReadUpdatedHandler);
    socket.on(NOTIFICATIONS_UPDATED, notificationsUpdatedHandler);

    const onReconnect = () => {
      invalidateRequestCaches(queryClient);
    };
    socket.io.on('reconnect', onReconnect);

    const onConnectError = () => {
      if (!getHasConnectedBefore()) return;
      void coordinatedRefresh().catch((e) => {
        if (e instanceof ApiClientError) {
          // Authoritative session loss (4xx from /auth/refresh):
          // expire the session locally and propagate to sister tabs so
          // the UI redirects to /login everywhere in this profile.
          const reason =
            e.code === 'AUTH_REFRESH_INVALID'
              ? 'AUTH_REFRESH_INVALID'
              : e.code === 'AUTH_REFRESH_REUSED'
                ? 'AUTH_REFRESH_REUSED'
                : 'AUTH_REFRESH_4XX';
          clearWithBroadcast(reason);
        }
        // NetworkError / ThrottleError / SessionChangedError:
        // transient — preserve session, retry later.
      });
    };
    socket.on('connect_error', onConnectError);

    const onDisconnect = (reason: string) => {
      // io server disconnect: the server explicitly closed the connection
      // (e.g. expired JWT). Socket.IO client does not auto-reconnect.
      //
      // socket.active === false with any reason: the transport layer
      // dropped without an intentional client disconnect — recoverable.
      const needsRecovery = reason === 'io server disconnect' || !socket.active;
      if (!needsRecovery) return;
      pendingRecoveryRef.current = true;
      coordinatedRefresh().then(
        () => {
          if (!useAuthStore.getState().accessToken) return;
          if (socket.connected) return;
          socket.connect();
        },
        (e) => {
          if (e instanceof ApiClientError) {
            // Authoritative session loss — clear, propagate to sister
            // tabs, and don't reconnect.
            const reason =
              e.code === 'AUTH_REFRESH_INVALID'
                ? 'AUTH_REFRESH_INVALID'
                : e.code === 'AUTH_REFRESH_REUSED'
                  ? 'AUTH_REFRESH_REUSED'
                  : 'AUTH_REFRESH_4XX';
            clearWithBroadcast(reason);
            return;
          }
          // Transient failure (NetworkError, ThrottleError, SessionChangedError):
          // schedule a reconnect on the next accessToken change.
          needsReconnectRef.current = true;
        },
      );
    };
    socket.on('disconnect', onDisconnect);

    const onConnectForRecovery = () => {
      if (pendingRecoveryRef.current) {
        pendingRecoveryRef.current = false;
        invalidateRequestCaches(queryClient);
      }
    };
    socket.on('connect', onConnectForRecovery);

    return () => {
      socket.off(CARGO_PROBLEM_WORKSPACE_UPDATED, cargoProblemWorkspaceHandler);
      socket.off(LEADS_UPDATED, leads.leadsUpdatedHandler);
      socket.off(LEAD_NOTIFICATIONS_UPDATED, leads.leadNotificationsUpdatedHandler);
      socket.off(REQUESTS_UPDATED, china.requestsUpdatedHandler);
      socket.off(REQUEST_CHAT_UPDATED, china.chatUpdatedHandler);
      socket.off(REQUEST_CHAT_READ_UPDATED, china.chatReadUpdatedHandler);
      socket.off(RUSSIA_REQUESTS_UPDATED, russia.russiaRequestsUpdatedHandler);
      socket.off(RUSSIA_REQUEST_CHAT_UPDATED, russia.russiaRequestChatUpdatedHandler);
      socket.off(RUSSIA_REQUEST_CHAT_READ_UPDATED, russia.russiaRequestChatReadUpdatedHandler);
      socket.off(NOTIFICATIONS_UPDATED, notificationsUpdatedHandler);
      socket.io.off('reconnect', onReconnect);
      socket.off('connect_error', onConnectError);
      socket.off('disconnect', onDisconnect);
      socket.off('connect', onConnectForRecovery);
    };
  }, [accessToken, queryClient]);

  useLifecycleCatchUp(accessToken, queryClient);

  return null;
}
